import React from "react";
import { Button, Dialog, DialogActions, DialogContent, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import ClosableDialogTitle from "common/ClosableDialogTitle";
import { gql, useMutation } from "@apollo/client";
import { InaType, InaTypeInput } from "./models";
import * as Yup from "yup";
import { Field as FormikField, Form as FormikForm, Formik } from "formik";
import { Select as FmuiSelect, TextField as FmuiTextField, Autocomplete, AutocompleteRenderInputParams } from "formik-mui";
import { Validations } from "common/validations/common-yup-validations";
import { UserRole } from "users";
import { LoadingButton } from "@mui/lab";
import { useNotifications } from "notifications";
import { makeStyles } from "../makeStyles";

const useStyles = makeStyles()((theme) => ({
  autocomplete: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  memberList: {
    paddingLeft: theme.spacing(1),
    fontSize: "0.75em",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
  },
}));

const EditInaTypeMutation = gql`
  mutation EditInaType($inaType: InaTypeInput!) {
    inaTypes {
      update(inaType: $inaType) {
        id
        friendlyName
        assignedToRoleNormally {
          id
          name
        }
        assignedToRoleForNonAssuranceReviews {
          id
          name
        }
        assignedToRoleForPprpProgramReviews {
          id
          name
        }
        reassignableToRoles {
          id
          name
        }
      }
    }
  }
`;

interface Props {
  handleClose: () => void;
  inaType: InaType;
  userRoles: UserRole[];
}

interface FormValues {
  friendlyName: string;
  assignedToRoleIdNormally: number | null;
  assignedToRoleIdForNonAssuranceReviews: number | null;
  assignedToRoleIdForPprpProgramReviews: number | null;
  reassignableToRoleIds: number[];
}

const EditWorkflowInaDialog = (props: Props) => {
  const { classes } = useStyles();
  const notifications = useNotifications();

  const validationSchema = Yup.object({
    friendlyName: Validations.requiredText(),
  });

  const [mutate, { loading }] = useMutation<{ inaTypes: { update: InaType } }, { inaType: InaTypeInput }>(EditInaTypeMutation);

  const listUserRoles = (userRole: UserRole) => (
    <MenuItem value={userRole.id} key={userRole.id}>
      {userRole.name}
    </MenuItem>
  );

  const sortedUserRoles = [...props.userRoles].sort((a, b) => a.name.localeCompare(b.name));

  const initialFormValues: FormValues = {
    friendlyName: props.inaType.friendlyName,
    assignedToRoleIdNormally: props.inaType.assignedToRoleNormally?.id ?? null,
    assignedToRoleIdForNonAssuranceReviews: props.inaType.assignedToRoleForNonAssuranceReviews?.id ?? null,
    assignedToRoleIdForPprpProgramReviews: props.inaType.assignedToRoleForPprpProgramReviews?.id ?? -1,
    reassignableToRoleIds: props.inaType.reassignableToRoles?.map((ur) => ur.id) ?? [],
  };

  return (
    <Dialog open={true} onClose={props.handleClose} fullWidth={true} scroll="paper" maxWidth="sm">
      <ClosableDialogTitle onClose={props.handleClose}>Edit Workflow INA Type</ClosableDialogTitle>
      <Formik
        initialValues={initialFormValues}
        onSubmit={async (values) => {
          let inaType: InaTypeInput = {
            id: props.inaType.id,
            friendlyName: values.friendlyName,
            assignedToRoleIdNormally: Number(values.assignedToRoleIdNormally),
            assignedToRoleIdForNonAssuranceReviews: Number(values.assignedToRoleIdForNonAssuranceReviews),
            assignedToRoleIdForPprpProgramReviews:
              values.assignedToRoleIdForPprpProgramReviews !== -1 ? Number(values.assignedToRoleIdForPprpProgramReviews) : null,
            reassignableToRoleIds: values.reassignableToRoleIds,
          };

          const result = await mutate({
            variables: { inaType },
          });

          if (result.data?.inaTypes.update) {
            notifications.success("Saved INA type.");
          }

          props.handleClose();
        }}
        validationSchema={validationSchema}
      >
        {(formikProps) => (
          <>
            <DialogContent>
              <FormikForm>
                <Grid container direction="column">
                  <Grid item>
                    <FormikField component={FmuiTextField} name="friendlyName" label="Description" fullWidth required />
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" fullWidth size="small">
                      <FormikField
                        label="Assign To (Default)"
                        name="assignedToRoleIdNormally"
                        value={sortedUserRoles.find((ur) => ur.id === formikProps.values.assignedToRoleIdNormally) || null}
                        onChange={(_: any, value: any) => {
                          formikProps.setFieldValue("assignedToRoleIdNormally", value ? value.id : null);
                        }}
                        renderInput={(params: any) => <TextField label="Assign To (Default)" {...params} margin="none" />}
                        className={classes.autocomplete}
                        component={Autocomplete}
                        options={sortedUserRoles}
                        disableClearable
                        getOptionLabel={(option: UserRole) => option.name}
                        renderOption={(liProps: React.HTMLAttributes<HTMLLIElement>, option: UserRole) => (
                          <li
                            {...liProps}
                            key={option.id ?? option}
                            className="MuiAutocomplete-option"
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            {typeof option === "object" && option.id ? (
                              <div>
                                <div>{option.name}</div>
                                <div className={classes.memberList}>
                                  {Array.isArray(option.members) && option.members.length > 0
                                    ? option.members.map((u) => u.name).join(", ")
                                    : "--"}
                                </div>
                              </div>
                            ) : (
                              <span>{String(option)}</span>
                            )}
                          </li>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" fullWidth size="small">
                      <FormikField
                        label="Assign To (Non-assurance PAF Reviews)"
                        name="assignedToRoleIdForNonAssuranceReviews"
                        value={sortedUserRoles.find((ur) => ur.id === formikProps.values.assignedToRoleIdForNonAssuranceReviews) || null}
                        onChange={(_: any, value: any) => {
                          formikProps.setFieldValue("assignedToRoleIdForNonAssuranceReviews", value ? value.id : null);
                        }}
                        renderInput={(params: any) => <TextField label="Assign To (Non-assurance PAF Reviews)" {...params} margin="none" />}
                        className={classes.autocomplete}
                        component={Autocomplete}
                        options={sortedUserRoles}
                        disableClearable
                        getOptionLabel={(option: UserRole) => option.name}
                        renderOption={(liProps: React.HTMLAttributes<HTMLLIElement>, option: UserRole) => (
                          <li
                            {...liProps}
                            key={option.id ?? option}
                            className="MuiAutocomplete-option"
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            {typeof option === "object" && option.id ? (
                              <div>
                                <div>{option.name}</div>
                                <div className={classes.memberList}>
                                  {Array.isArray(option.members) && option.members.length > 0
                                    ? option.members.map((u) => u.name).join(", ")
                                    : "--"}
                                </div>
                              </div>
                            ) : (
                              <span>{String(option)}</span>
                            )}
                          </li>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" fullWidth size="small">
                      <FormikField
                        label="Assign To (PPRP Program Reviews)"
                        name="assignedToRoleIdForPprpProgramReviews"
                        value={sortedUserRoles.find((ur) => ur.id === formikProps.values.assignedToRoleIdForPprpProgramReviews) || null}
                        onChange={(_: any, value: any) => {
                          formikProps.setFieldValue("assignedToRoleIdForPprpProgramReviews", value ? value.id : null);
                        }}
                        renderInput={(params: any) => <TextField label="Assign To (PPRP Program Reviews)" {...params} margin="none" />}
                        className={classes.autocomplete}
                        component={Autocomplete}
                        options={sortedUserRoles}
                        disableClearable
                        getOptionLabel={(option: UserRole) => option.name}
                        renderOption={(liProps: React.HTMLAttributes<HTMLLIElement>, option: UserRole) => (
                          <li
                            {...liProps}
                            key={option.id ?? option}
                            className="MuiAutocomplete-option"
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            {typeof option === "object" && option.id ? (
                              <div>
                                <div>{option.name}</div>
                                <div className={classes.memberList}>
                                  {Array.isArray(option.members) && option.members.length > 0
                                    ? option.members.map((u) => u.name).join(", ")
                                    : "--"}
                                </div>
                              </div>
                            ) : (
                              <span>{String(option)}</span>
                            )}
                          </li>
                        )}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </FormikForm>
            </DialogContent>
            <DialogActions>
              <Button onClick={props.handleClose}>Cancel</Button>
              <LoadingButton color="primary" variant="contained" loading={loading} onClick={() => formikProps.submitForm()}>
                Save
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditWorkflowInaDialog;
